import React, { useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { permissionApi } from '@/services/permissionsRolesApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Save, AlertCircle } from 'lucide-react';
import { LoadingState, ErrorState } from '@/components/common';
import type { Permission, PermissionRequest, PermissionUpdateRequest, PermissionAction } from '@/types/permissionsRoles';

// Zod schema
const permissionSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(100, 'Name must be at most 100 characters'),
  description: z.string().max(500, 'Description must be at most 500 characters').optional().or(z.literal('')),
  resource: z.string().optional().or(z.literal('')),
  action: z.enum(['CREATE', 'READ', 'UPDATE', 'DELETE', 'MANAGE']).optional().or(z.literal('')),
  active: z.boolean().optional(),
});

type PermissionFormData = z.infer<typeof permissionSchema>;

const ACTIONS: PermissionAction[] = ['CREATE', 'READ', 'UPDATE', 'DELETE', 'MANAGE'];

export const PermissionFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { hasPermission } = useAdminAuth();

  const canCreate = hasPermission('CREATE_PERMISSION', 'permission');
  const canEdit = hasPermission('UPDATE_PERMISSION', 'permission');

  const form = useForm<PermissionFormData>({
    resolver: zodResolver(permissionSchema),
    defaultValues: {
      name: '',
      description: '',
      resource: '',
      action: undefined,
      active: true,
    },
  });

  const watchedName = form.watch('name');

  // Debounced uniqueness check
  useEffect(() => {
    if (!isEdit && watchedName && watchedName.length >= 2) {
      const timeoutId = setTimeout(async () => {
        // In a real implementation, you'd call an API endpoint to check uniqueness
        // For now, we'll just clear any existing errors
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [watchedName, isEdit]);

  // Memoize the fetch function to prevent useEntity from recreating on every render
  const fetchPermissionById = useCallback((permissionId: string) => permissionApi.getById(permissionId), []);

  // Fetch permission data for edit mode
  const {
    data: _permission, // Permission data is used in onSuccess callback, not directly in component
    isLoading: isLoadingPermission,
    error: permissionError,
  } = useEntity<Permission>({
    fetchFn: fetchPermissionById,
    entityId: id,
    entityName: 'permission',
    enabled: isEdit && !!id && canEdit,
    onSuccess: (data) => {
      console.log('[PermissionFormPage] Permission loaded:', {
        permissionId: data.id,
        permissionName: data.name,
        isSystemPermission: data.systemPermission,
      });
      
      form.reset({
        name: data.name,
        description: data.description || '',
        resource: data.resource || '',
        action: data.action || undefined,
        active: data.active,
      });
    },
    onError: () => {
      navigate('/admin/permissions');
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: PermissionFormData) => {
      const payload: PermissionRequest = {
        name: data.name,
        description: data.description || undefined,
        resource: data.resource || undefined,
        action: data.action || undefined,
        active: data.active ?? true,
      };
      return await permissionApi.create(payload);
    },
    entityName: 'permission',
    operation: 'create',
    onSuccess: () => {
      navigate('/admin/permissions');
    },
  });

  // Update mutation
  // Note: Name cannot be updated - only description, resource, action, and active can be changed
  const updateMutation = useMutation({
    mutationFn: async (data: PermissionFormData) => {
      if (!id) throw new Error('Permission ID is required');
      // For updates, only send fields that can be changed (name is NOT included)
      const payload: PermissionUpdateRequest = {
        description: data.description || undefined,
        resource: data.resource || undefined,
        action: data.action || undefined,
        active: data.active ?? true,
      };
      return await permissionApi.update(id, payload);
    },
    entityName: 'permission',
    operation: 'update',
    onSuccess: () => {
      navigate('/admin/permissions');
    },
  });

  const onSubmit = async (data: PermissionFormData) => {
    if (isEdit) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const isLoading = isLoadingPermission;
  const mutation = isEdit ? updateMutation : createMutation;

  if (!canCreate && !isEdit) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">You do not have permission to create permissions.</p>
        <Button asChild className="mt-4">
          <Link to="/admin/permissions">Back to List</Link>
        </Button>
      </div>
    );
  }

  if (isEdit && !canEdit) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">You do not have permission to edit permissions.</p>
        <Button asChild className="mt-4">
          <Link to="/admin/permissions">Back to List</Link>
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingState message="Loading permission..." />;
  }

  if (isEdit && permissionError) {
    return <ErrorState error={permissionError} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/permissions">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEdit ? 'Edit Permission' : 'Create Permission'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isEdit ? 'Update permission information' : 'Create a new permission'}
            </p>
          </div>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Permission Information</CardTitle>
              <CardDescription>Enter permission details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isEdit && (
                <div className="flex items-start gap-3 p-3 rounded-md bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                  <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      Permission name cannot be changed
                    </p>
                    <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                      Once a permission is created, its name cannot be modified. You can update the description, resource, action, and active status.
                    </p>
                  </div>
                </div>
              )}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter permission name"
                        {...field}
                        disabled={isEdit}
                      />
                    </FormControl>
                    <FormDescription>
                      {isEdit ? (
                        <span className="text-muted-foreground">
                          Permission name cannot be changed after creation. {watchedName.length}/100 characters.
                        </span>
                      ) : (
                        <span>{watchedName.length}/100 characters (minimum 2)</span>
                      )}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter permission description"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      {field.value?.length || 0}/500 characters
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="resource"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Resource</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter resource" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="action"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Action</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || ''}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select action" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {ACTIONS.map((action) => (
                            <SelectItem key={action} value={action}>
                              {action}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="active"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2">
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value ?? true}
                        onChange={field.onChange}
                        className="rounded border-gray-300"
                      />
                    </FormControl>
                    <FormLabel>Active</FormLabel>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link to="/admin/permissions">Cancel</Link>
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                form.setValue('active', false);
                form.handleSubmit(onSubmit)();
              }}
              disabled={mutation.isLoading}
            >
              Save as Draft
            </Button>
            <Button type="submit" disabled={mutation.isLoading}>
              <Save className="mr-2 h-4 w-4" />
              {mutation.isLoading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default PermissionFormPage;
